import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { showNotification } from '../../App';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { EmailTemplateListQueryData, EmailTemplateListQueryParams, EmailTemplateTypeEnum, retrieveEmailTemplateListData } from '../EmailTemplateRespository';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        width: '100%'
    },
  })
);

interface EmailResponseTemplateSelectorProps {
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem[];
    error?: boolean;
    // tslint:disable-next-line: no-any
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[] | any | any[], name: string) => void;
    className?: string;
    helperText?: string;
    name?: string;
    label?: string;
    disablePortal?: boolean;
    disableCloseOnSelect?: boolean;
    size?: 'small' | 'medium';
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    // Explicit bug report: this was hardcoded to 'standard' (underlined, no border) on the
    // underlying TextField with no way for a caller to ask for anything else - inconsistent
    // next to every other outlined selector on the same panel. Optional, defaulting to the
    // existing 'standard' so the other 3 call sites of this component keep their current look
    // unchanged; only the one call site with this complaint passes 'outlined'.
    variant?: 'standard' | 'outlined' | 'filled';
}

interface EmailResponseTemplateSelectorState {
    isLoading: boolean;
    // tslint:disable-next-line: no-any
    options: IAutoCompleteItem[];
}

export const EmailResponseTemplateSelector: React.FC<EmailResponseTemplateSelectorProps> = props => {

    const classes = useStyles();

    const [state, setState] = useState<EmailResponseTemplateSelectorState>({
        isLoading: false,
        options: [],
    });

    useEffect(() => {   
        fetchData();
    // tslint:disable-next-line: align
    }, []);

    const fetchData = () => {

        setState({
            ...state,
            isLoading: true
        });

        let emailTemplateQueryParams: EmailTemplateListQueryParams = {
            emailType: EmailTemplateTypeEnum[EmailTemplateTypeEnum.EMAIL_RESPONSE],
            messageType: 'EMAIL',
            integrationTemplateId: null,
            isActive: true,
        };

    // tslint:disable-next-line: align
    retrieveEmailTemplateListData(
        emailTemplateQueryParams,
        false,
        // tslint:disable-next-line: no-any
        (data: any) => onDataRetrieved(data),
        // tslint:disable-next-line:no-any
        function (reason: any): void {
            showNotification(null, reason, 'error');
        });
    };

    const onDataRetrieved = (data: EmailTemplateListQueryData) => {  
        setState((prevState) => {
            return {
                ...prevState,
                isLoading: false,
                options: data.templates.emailTemplateList.map((item) => {
                    return {
                        label: item.name,
                        value: item.emailTemplateGuid
                    };
                })
            };
        });
    };

    // tslint:disable-next-line: no-any
    const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
        // tslint:disable-next-line: no-console
        if (props.onSelection) {
            props.onSelection(value, props.name ? props.name : 'areaOfLaw');
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    return (
        <div className={`${classes.root} ${props.className}`}>
            { props.multiple ? 
                (
                    <Autocomplete
                        // [] (not null) is MUI's "nothing selected" sentinel for multiple={true}.
                        value={Array.isArray(props.value) ? props.value : []}
                        multiple={true}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.options}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}       
                        getOptionSelected={(option, value) => value.value === option.value}        
                        size={props.size ? props.size : 'medium'}           
                        onBlur={props.onBlur}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label ? props.label : 'Email Template'}
                                variant={props.variant || 'standard'}
                                fullWidth={true}
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}      
                                helperText={props.helperText}       
                                size={props.size ? props.size : 'medium'}                     
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            );
                        }}
                    />
                )
                :
                (
                    <Autocomplete
                        // Explicit bug report: the raw field value (e.g. '' before anything is
                        // selected) was passed straight through - not an {label,value}-shaped
                        // object matching `options` - causing MUI's getOptionLabel to be called
                        // on a non-object and log "getOptionLabel returned undefined...". null
                        // is MUI's real "nothing selected" sentinel.
                        value={props.value && typeof props.value === 'object' ? props.value : null}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.options}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}    
                        getOptionSelected={(option, value) => value.value === option.value}    
                        size={props.size ? props.size : 'medium'} 
                        onBlur={props.onBlur}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label ? props.label : 'Email Template'}
                                variant={props.variant || 'standard'}
                                fullWidth={true}
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}    
                                helperText={props.helperText}           
                                size={props.size ? props.size : 'medium'}             
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                    </span>
                                ))}
                                </div>
                            );
                        }}
                    />
                )
            }
        </div>
    );
};